import React, { useMemo } from 'react';
import ReactModal from 'react-modal';

import type { SafariCompatModalProps as ModalProps } from '../../../../styles/SafariCompatModal';
import { SafariCompatModal as Modal } from '../../../../styles/SafariCompatModal';
import * as styles from '../styles/Popover.styles';
import type { PointerAlignment } from 'client-ribbons-design-system/components/elements/Pointer/Pointer';
import { Pointer } from 'client-ribbons-design-system/components/elements/Pointer/Pointer';

const CLOSE_TIMEOUT_MS = 250;

export interface PopoverProps extends ModalProps {
  contentLabel: string;
  dimensions?: {
    height?: number;
    width?: number;
  };
  isOpen: boolean;
  onAfterClose?: () => void;
  onRequestClose: () => void;
  pointerAlignment: PointerAlignment;
  pointerCustomValue?: number;
  position?: {
    bottom?: number;
    left?: number;
    right?: number;
    top?: number;
  };
}

const POPOVER_CONTENT_CLASS_NAMES: styles.PopoverGlobalStylesProps['contentClassNames'] = {
  base: 'popover-content',
  afterOpen: 'popover-content--afterOpen',
  beforeClose: 'popover-content--beforeClose',
};

const POPOVER_OVERLAY_CLASS_NAMES: styles.PopoverGlobalStylesProps['overlayClassNames'] = {
  base: 'popover-overlay',
  afterOpen: 'popover-overlay--afterOpen',
  beforeClose: 'popover-overlay--beforeClose',
};

const PopoverGlobalStyles = styles.PopoverGlobalStyles({
  contentClassNames: POPOVER_CONTENT_CLASS_NAMES,
  overlayClassNames: POPOVER_OVERLAY_CLASS_NAMES,
  closeTimeoutMS: CLOSE_TIMEOUT_MS,
});

const getCSSValue = (value?: number) => (value ? `${value}px` : 'auto');

/*
  The Popover component displays sub components within a modal. It can be provided
  dimensions and position props.
*/
export const Popover = ({
  bodyOpenClassName,
  children,
  contentLabel,
  isOpen,
  onAfterClose,
  onRequestClose,
  pointerAlignment,
  pointerCustomValue,
  shouldReturnFocusAfterClose,
  ...props
}: PopoverProps) => {
  const contentStyle = useMemo(
    () => ({
      content: {
        width: getCSSValue(props.dimensions?.width),
        height: getCSSValue(props.dimensions?.height),
        top: getCSSValue(props.position?.top),
        bottom: getCSSValue(props.position?.bottom),
        left: getCSSValue(props.position?.left),
        right: getCSSValue(props.position?.right),
      },
    }),
    [
      props.dimensions?.height,
      props.dimensions?.width,
      props.position?.bottom,
      props.position?.left,
      props.position?.right,
      props.position?.top,
    ],
  );

  return (
    <>
      <PopoverGlobalStyles />
      <Modal
        ariaHideApp={false}
        bodyOpenClassName={bodyOpenClassName}
        className={POPOVER_CONTENT_CLASS_NAMES}
        closeTimeoutMS={CLOSE_TIMEOUT_MS}
        contentLabel={contentLabel}
        isOpen={isOpen}
        onAfterClose={onAfterClose}
        onRequestClose={onRequestClose}
        overlayClassName={POPOVER_OVERLAY_CLASS_NAMES}
        shouldReturnFocusAfterClose={shouldReturnFocusAfterClose}
        style={contentStyle}
      >
        <>
          {children}
          <Pointer alignment={pointerAlignment} pointerCustomLeftValue={pointerCustomValue} />
        </>
      </Modal>
    </>
  );
};

export const setPopoverAppElement = ReactModal.setAppElement;
